import React from "react";
import PropTypes from "prop-types";
import { CSSTransition } from "react-transition-group";
import useEmblaCarousel from "embla-carousel-react";
//Material imports
import withStyles from "@mui/styles/withStyles";
import Typography from "@mui/material/Typography";
import Fullscreen from "@mui/icons-material/Fullscreen";
import Close from "@mui/icons-material/Close";
//Limitless imports
import { withi18n } from "../../hocs/i18n";
import UserAvatar from "../../user-avatar/UserAvatar";
import TimePosted from "../../time-posted/TimePosted";
import DialogueBody from "../../dialogue-body/DialogueBody";
import SlideIndicator from "../../carousel/slide-indicator/SlideIndicator";
const wordCount = 170;
const truncate = body => {
  if (body.length < wordCount) {
    return body;
  }
  const subString = body.substr(0, wordCount - 1);
  return `${subString.substr(0, subString.lastIndexOf(" "))}...`;
};
const formatConfidence = answer => {
  return Math.round(answer.suggestedAnswerScore * 100);
};
const SuggestedAnswerDialogueCarousel = ({
  theme,
  classes,
  dialogue,
  message,
  handleSuggestedAnswerSelection,
  handleFullScreen,
  t,
  unmount,
  logger
}) => {
  const isRtl = document.documentElement.dir === "rtl";
  const [saState, setSAState] = React.useState({
    value: 0,
    fullScreen: false,
    currentAnswer: {}
  });
  const [emblaRef, emblaApi] = useEmblaCarousel({
    draggable: false,
    speed: 15,
    direction: isRtl ? "rtl" : "ltr"
  });
  React.useEffect(() => {
    if (emblaApi) {
      emblaApi.scrollTo(saState.value);
    }
  }, [emblaApi, saState.value]);
  React.useEffect(() => {
    emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.reInit();
  }, [saState.fullScreen]);
  React.useEffect(() => {
    if (dialogue.suggestedAnswers) {
      handleSuggestedAnswerSelection(dialogue.suggestedAnswers[0].dialogueId);
      setSAState({
        ...saState,
        currentAnswer: dialogue.suggestedAnswers[0]
      });
    }
  }, []);
  const renderAnswers = () => {
    const {
      suggestedAnswers
    } = dialogue;
    const {
      fullScreen
    } = saState;
    return suggestedAnswers.map((answer, index) => {
      return /*#__PURE__*/React.createElement("div", {
        id: `sa-${index + 1}`,
        className: "embla__slide",
        key: index,
        style: {
          width: fullScreen ? 320 : 230
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: classes.responseListItem,
        onClick: handleClick
      }, fullScreen ? /*#__PURE__*/React.createElement(DialogueBody, {
        body: answer.body,
        textStyling: classes.secondaryText
      }) : /*#__PURE__*/React.createElement(Typography, {
        dir: theme.direction,
        key: index,
        className: `${classes.secondaryText}`,
        component: "span",
        tabIndex: 0
      }, truncate(answer.body))));
    });
  };
  const renderAvatar = () => {
    const expertInfo = message.expertAuthors[dialogue.userId];
    return /*#__PURE__*/React.createElement(UserAvatar, {
      thumbnail: expertInfo ? expertInfo.thumbnail : null,
      author: dialogue.author
    });
  };
  const handleChange = (event, value) => {
    setSAState({
      ...saState,
      value,
      currentAnswer: dialogue.suggestedAnswers[value]
    });
    const selectedDialogueId = dialogue.suggestedAnswers[value].dialogueId;
    handleSuggestedAnswerSelection(selectedDialogueId);
  };
  const handleClick = () => {
    setSAState({
      ...saState,
      fullScreen: !saState.fullScreen
    });
    handleFullScreen(saState.fullScreen);
  };
  const {
    created,
    suggestedAnswers
  } = dialogue;
  const {
    fullScreen,
    currentAnswer
  } = saState;
  const moreThanOneSA = suggestedAnswers.length >= 2;
  // for any reason suggested answers is not available, return a div.
  if (!suggestedAnswers) {
    return /*#__PURE__*/React.createElement("div", null);
  }
  //TODO refactor this mess so it's 2 components, 1 fullscreen and one normal
  logger.debug(`${suggestedAnswers.length} SAs`);
  return /*#__PURE__*/React.createElement(CSSTransition, {
    in: !unmount,
    timeout: 300,
    unmountOnExit: true,
    classNames: {
      exit: classes.fadeExit,
      exitActive: classes.fadeExitActive
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.saContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.listItem
  }, /*#__PURE__*/React.createElement("div", null, renderAvatar()), /*#__PURE__*/React.createElement("div", {
    className: `${classes.responseWrapper} ${classes.secondaryBackground} ${fullScreen ? classes.fullScreen : ""}`
  }, /*#__PURE__*/React.createElement("div", {
    className: `${classes.header} ${fullScreen && classes.fullScreenHeader}`,
    onClick: handleClick
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.headerFiller
  }), /*#__PURE__*/React.createElement(Typography, {
    color: "inherit",
    classes: {
      root: classes.titleRoot
    },
    align: "center",
    tabIndex: 0
  }, t("Previous Answers Given")), fullScreen ? /*#__PURE__*/React.createElement(Close, {
    className: `${classes.secondaryText} ${classes.headerIcon}`
  }) : /*#__PURE__*/React.createElement(Fullscreen, {
    className: `${classes.secondaryText} ${classes.headerIcon}`,
    tabIndex: 0
  })), /*#__PURE__*/React.createElement("div", {
    className: "embla",
    ref: emblaRef,
    style: {
      height: "unset"
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "embla__container embla__container_hiw"
  }, renderAnswers())), /*#__PURE__*/React.createElement("div", {
    className: classes.confidenceContainer,
    style: !moreThanOneSA ? {
      paddingBottom: 5
    } : {}
  }, currentAnswer && currentAnswer.suggestedAnswerScore && /*#__PURE__*/React.createElement(Typography, {
    className: classes.confidenceText,
    variant: "caption"
  }, t("{0}% confidence", formatConfidence(currentAnswer)))), moreThanOneSA && /*#__PURE__*/React.createElement(SlideIndicator, {
    slideCount: suggestedAnswers.length,
    onSlideChange: (event, value) => handleChange(event, value),
    activeSlideIndex: saState.value,
    classes: {
      activeIndicator: classes.activeIndicator,
      tabRoot: classes.indicator
    }
  }))), /*#__PURE__*/React.createElement("div", {
    className: classes.responseTime
  }, /*#__PURE__*/React.createElement(TimePosted, {
    time: created
  }))));
};
const styles = ({
  palette,
  typography
}) => {
  var _palette$dialogues$su, _palette$dialogues, _palette$dialogues$su2, _palette$dialogues$su3, _palette$dialogues2, _palette$dialogues2$s, _palette$dialogues$su4, _palette$dialogues3, _palette$dialogues3$s, _palette$dialogues$su5, _palette$dialogues4, _palette$dialogues4$s, _palette$dialogues$su6, _palette$dialogues5, _palette$dialogues5$s;
  const light = (_palette$dialogues$su = (_palette$dialogues = palette.dialogues) === null || _palette$dialogues === void 0 ? void 0 : (_palette$dialogues$su2 = _palette$dialogues.suggestedAnswer) === null || _palette$dialogues$su2 === void 0 ? void 0 : _palette$dialogues$su2.background) !== null && _palette$dialogues$su !== void 0 ? _palette$dialogues$su : palette.secondary.light;
  const dark = (_palette$dialogues$su3 = (_palette$dialogues2 = palette.dialogues) === null || _palette$dialogues2 === void 0 ? void 0 : (_palette$dialogues2$s = _palette$dialogues2.suggestedAnswer) === null || _palette$dialogues2$s === void 0 ? void 0 : _palette$dialogues2$s.text) !== null && _palette$dialogues$su3 !== void 0 ? _palette$dialogues$su3 : palette.secondary.dark;
  const main = (_palette$dialogues$su4 = (_palette$dialogues3 = palette.dialogues) === null || _palette$dialogues3 === void 0 ? void 0 : (_palette$dialogues3$s = _palette$dialogues3.suggestedAnswer) === null || _palette$dialogues3$s === void 0 ? void 0 : _palette$dialogues3$s.background) !== null && _palette$dialogues$su4 !== void 0 ? _palette$dialogues$su4 : palette.secondary.main;
  const borderRadius = (_palette$dialogues$su5 = (_palette$dialogues4 = palette.dialogues) === null || _palette$dialogues4 === void 0 ? void 0 : (_palette$dialogues4$s = _palette$dialogues4.suggestedAnswer) === null || _palette$dialogues4$s === void 0 ? void 0 : _palette$dialogues4$s.borderRadius) !== null && _palette$dialogues$su5 !== void 0 ? _palette$dialogues$su5 : 12;
  return {
    saContainer: {
      paddingTop: 10,
      paddingBottom: 10
    },
    header: {
      borderBottom: "solid 1px",
      borderBottomColor: dark,
      color: dark,
      display: "flex",
      justifyContent: "space-between",
      minHeight: 35
    },
    headerFiller: {
      height: 20,
      width: 20
    },
    "@media screen and (min-width: 450px)": {
      fullScreenHeader: {
        height: 65
      }
    },
    titleRoot: {
      padding: "8px"
    },
    headerIcon: {
      size: 12,
      paddingTop: 5,
      paddingRight: 10
    },
    secondaryBackground: {
      backgroundColor: main
    },
    secondaryText: {
      color: dark,
      fontFamily: (_palette$dialogues$su6 = (_palette$dialogues5 = palette.dialogues) === null || _palette$dialogues5 === void 0 ? void 0 : (_palette$dialogues5$s = _palette$dialogues5.suggestedAnswer) === null || _palette$dialogues5$s === void 0 ? void 0 : _palette$dialogues5$s.fontFamily) !== null && _palette$dialogues$su6 !== void 0 ? _palette$dialogues$su6 : typography.fontFamily
    },
    author: {
      paddingLeft: 10,
      paddingTop: 3,
      borderTop: "solid 1px",
      borderTopColor: dark
    },
    indicator: {
      minWidth: 0,
      margin: 5,
      height: 12,
      width: 12,
      borderRadius: "50%",
      minHeight: 12,
      //for IE11,
      padding: 0,
      backgroundColor: light,
      border: `0.5px solid ${dark}`
    },
    activeIndicator: {
      backgroundColor: dark,
      height: 12,
      width: 12,
      minHeight: 12,
      //for IE11,
      borderRadius: "50%",
      padding: 0,
      margin: 5,
      border: `0.5px solid ${dark}`
    },
    listItem: {
      display: "flex",
      flex: 1,
      flexDirection: "row",
      width: "85%",
      marginLeft: 5
    },
    responseWrapper: {
      flex: 1,
      marginLeft: 5,
      marginRight: 5,
      position: "relative",
      borderRadius,
      flexDirection: "column",
      cursor: "pointer",
      maxWidth: "85%",
      "-webkit-tap-highlight-color": "transparent"
    },
    responseListItem: {
      display: "flex",
      flex: 1,
      padding: "10px 10px 0px 10px",
      flexDirection: "column"
    },
    confidenceContainer: {
      display: "flex",
      flex: 1,
      justifyContent: "flex-end",
      alignItems: "flex-end",
      minHeight: 20,
      paddingRight: 10
    },
    confidenceText: {
      color: dark,
      fontWeight: "600 !important"
    },
    fullScreen: {
      position: "absolute",
      padding: 0,
      margin: 0,
      right: 0,
      top: 0,
      left: 0,
      height: "100%",
      flex: 1,
      borderRadius: 0,
      maxWidth: "100%",
      display: "flex"
    },
    responseTime: {
      display: "flex",
      flex: 1,
      width: "85%",
      justifyContent: "flex-end",
      paddingTop: 5
    },
    fadeExit: {
      opacity: 1
    },
    fadeExitActive: {
      opacity: 0,
      transition: "all 300ms ease-in"
    }
  };
};
export default withStyles(styles, {
  withTheme: true
})(withi18n(SuggestedAnswerDialogueCarousel, "Suggested Answers Carousel"));
SuggestedAnswerDialogueCarousel.propTypes = {
  logger: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  handleSuggestedAnswerSelection: PropTypes.func.isRequired,
  handleFullScreen: PropTypes.func.isRequired,
  dialogue: PropTypes.object,
  unmount: PropTypes.bool.isRequired,
  message: PropTypes.object.isRequired
};